/**
 * A2UI 图表组件 - 基于 ApexCharts
 * 支持雷达图、柱状图、饼图、折线图等
 */
import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import './A2UIChart.css';

// ApexCharts 深色主题基础配置
const darkThemeBase = {
  theme: { mode: 'dark' },
  chart: {
    background: 'transparent',
    foreColor: '#b0b0b0',
    toolbar: { show: false },
    animations: {
      enabled: true,
      easing: 'easeinout',
      speed: 800,
      animateGradually: {
        enabled: true,
        delay: 150
      },
      dynamicAnimation: {
        enabled: true,
        speed: 350
      }
    },
    dropShadow: {
      enabled: false // 禁用阴影以提升性能
    }
  },
  grid: {
    borderColor: 'rgba(255, 255, 255, 0.08)',
    strokeDashArray: 3
  },
  tooltip: {
    theme: 'dark',
    style: {
      fontSize: '12px'
    },
    y: {
      formatter: (val) => val
    }
  },
  colors: ['#4d8bff', '#00d4aa', '#ff6b6b', '#ffc107', '#9c27b0', '#00bcd4']
};

// 雷达图配置
const getRadarOptions = (title, labels, values) => {
  // 智能量程调整：
  // 1. 如果最大值 <= 5，量程为 0-5
  // 2. 如果最大值 <= 10，量程为 0-10
  // 3. 否则默认 0-100（百分制）
  const maxValue = Math.max(...values);
  let yAxisMax = 100;
  if (maxValue <= 5) yAxisMax = 5;
  else if (maxValue <= 10) yAxisMax = 10;

  return {
    ...darkThemeBase,
    chart: {
      ...darkThemeBase.chart,
      type: 'radar',
      toolbar: { show: false }
    },
    title: {
      text: title,
      align: 'center',
      style: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#e0e0e0',
        fontFamily: 'Inter, system-ui, sans-serif'
      }
    },
    xaxis: {
      categories: labels,
      labels: {
        style: {
          colors: labels.map(() => 'rgba(255, 255, 255, 0.8)'),
          fontSize: '12px',
          fontWeight: 500,
          fontFamily: 'Inter, system-ui, sans-serif'
        }
      }
    },
    yaxis: {
      show: false,
      min: 0,
      max: yAxisMax,
      tickAmount: 4
    },
    stroke: {
      width: 2,
      colors: ['#4d8bff'],
      dashArray: 0
    },
    fill: {
      opacity: 0.4,
      colors: ['#4d8bff']
    },
    markers: {
      size: 4,
      colors: ['#4d8bff'],
      strokeColors: '#fff',
      strokeWidth: 2,
      hover: {
        size: 6
      }
    },
    plotOptions: {
      radar: {
        size: 110,
        polygons: {
          strokeColors: 'rgba(255, 255, 255, 0.15)',
          connectorColors: 'rgba(255, 255, 255, 0.15)',
          fill: {
            colors: ['rgba(255, 255, 255, 0.05)', 'transparent']
          }
        }
      }
    },
    tooltip: {
      theme: 'dark',
      y: {
        formatter: (val) => val
      }
    }
  };
};

// 柱状图配置
const getBarOptions = (title, labels) => ({
  ...darkThemeBase,
  chart: {
    ...darkThemeBase.chart,
    type: 'bar',
    toolbar: { show: false }
  },
  title: {
    text: title,
    align: 'center',
    style: {
      fontSize: '14px',
      fontWeight: 600,
      color: '#e0e0e0',
      fontFamily: 'Inter, system-ui, sans-serif'
    }
  },
  plotOptions: {
    bar: {
      borderRadius: 6, // 更圆润的角
      horizontal: false,
      columnWidth: '55%',
      distributed: true,
      dataLabels: {
        position: 'top'
      }
    }
  },
  dataLabels: {
    enabled: true,
    offsetY: -22,
    style: {
      fontSize: '12px',
      fontWeight: 600,
      colors: ['#b0b0b0']
    }
  },
  xaxis: {
    categories: labels,
    labels: {
      style: {
        colors: labels.map(() => '#b0b0b0'), // 数组格式
        fontSize: '12px'
      }
    },
    axisBorder: {
      color: 'rgba(255, 255, 255, 0.1)'
    },
    axisTicks: {
      color: 'rgba(255, 255, 255, 0.1)'
    }
  },
  yaxis: {
    labels: {
      style: {
        colors: '#b0b0b0',
        fontSize: '11px'
      }
    },
    min: 0
  },
  legend: {
    show: false
  },
  fill: {
    type: 'gradient',
    gradient: {
      shade: 'dark',
      type: 'vertical',
      shadeIntensity: 0.5,
      gradientToColors: undefined,
      inverseColors: true,
      opacityFrom: 1,
      opacityTo: 0.8,
      stops: [0, 100]
    }
  }
});

// 饼图配置
const getPieOptions = (title, labels) => ({
  ...darkThemeBase,
  chart: {
    ...darkThemeBase.chart,
    type: 'pie',
    toolbar: { show: false }
  },
  title: {
    text: title,
    align: 'center',
    style: {
      fontSize: '14px',
      fontWeight: 600,
      color: '#e0e0e0',
      fontFamily: 'Inter, system-ui, sans-serif'
    }
  },
  labels: labels,
  legend: {
    position: 'bottom',
    labels: {
      colors: '#b0b0b0'
    },
    fontSize: '12px',
    markers: {
      radius: 12
    },
    itemMargin: {
      horizontal: 10,
      vertical: 5
    }
  },
  stroke: {
    width: 1,
    colors: ['rgba(0,0,0,0.5)']
  },
  dataLabels: {
    enabled: true,
    style: {
      fontSize: '12px',
      fontWeight: 500,
      colors: ['#fff']
    },
    dropShadow: {
      enabled: true,
      blur: 2,
      opacity: 0.5
    }
  },
  plotOptions: {
    pie: {
      expandOnClick: true,
      customScale: 1
    }
  }
});

// 折线图配置
const getLineOptions = (title, labels) => ({
  ...darkThemeBase,
  chart: {
    ...darkThemeBase.chart,
    type: 'area', // 改为 area
    toolbar: { show: false }
  },
  title: {
    text: title,
    align: 'center',
    style: {
      fontSize: '14px',
      fontWeight: 600,
      color: '#e0e0e0',
      fontFamily: 'Inter, system-ui, sans-serif'
    }
  },
  stroke: {
    curve: 'smooth',
    width: 3
  },
  xaxis: {
    categories: labels,
    labels: {
      style: {
        colors: labels.map(() => '#b0b0b0'),
        fontSize: '12px'
      }
    },
    axisBorder: {
      color: 'rgba(255, 255, 255, 0.1)'
    },
    tooltip: {
      enabled: false
    }
  },
  yaxis: {
    labels: {
      style: {
        colors: '#b0b0b0',
        fontSize: '11px'
      }
    }
  },
  markers: {
    size: 5,
    colors: ['#fff'],
    strokeColors: '#4d8bff',
    strokeWidth: 2,
    hover: {
      size: 7
    }
  },
  fill: {
    type: 'gradient',
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.7,
      opacityTo: 0.2,
      stops: [0, 90, 100]
    }
  }
});

// 环形图配置
const getDonutOptions = (title, labels) => ({
  ...darkThemeBase,
  chart: {
    ...darkThemeBase.chart,
    type: 'donut',
  },
  title: {
    text: title,
    align: 'center',
    style: {
      fontSize: '14px',
      fontWeight: 500,
      color: '#e0e0e0'
    }
  },
  labels: labels,
  legend: {
    position: 'bottom',
    labels: {
      colors: '#b0b0b0'
    },
    fontSize: '11px'
  },
  stroke: {
    width: 1,
    colors: ['#1a1a2e']
  },
  plotOptions: {
    pie: {
      donut: {
        size: '55%',
        labels: {
          show: true,
          name: {
            show: true,
            fontSize: '12px',
            color: '#b0b0b0'
          },
          value: {
            show: true,
            fontSize: '16px',
            color: '#e0e0e0',
            fontWeight: 600
          },
          total: {
            show: true,
            label: '总计',
            fontSize: '12px',
            color: '#b0b0b0'
          }
        }
      }
    }
  },
  dataLabels: {
    enabled: false
  }
});

/**
 * A2UI 图表组件
 * @param {string} type - 图表类型: radar, bar, pie, line, donut
 * @param {string} title - 图表标题
 * @param {Array} data - 数据数组 [{label: string, value: number}]
 */
export const A2UIChart = ({ type = 'bar', title = '', data = [] }) => {
  // 提取标签和数值
  const labels = useMemo(() => data.map(d => d.label), [data]);
  const values = useMemo(() => data.map(d => d.value), [data]);

  // 根据类型生成配置
  const { options, series, chartType } = useMemo(() => {
    switch (type) {
      case 'radar':
        return {
          options: getRadarOptions(title, labels, values),
          series: [{ name: '得分', data: values }],
          chartType: 'radar'
        };
      case 'bar':
        return {
          options: getBarOptions(title, labels),
          series: [{ name: '数值', data: values }],
          chartType: 'bar'
        };
      case 'pie':
        return {
          options: getPieOptions(title, labels),
          series: values,
          chartType: 'pie'
        };
      case 'donut':
        return {
          options: getDonutOptions(title, labels),
          series: values,
          chartType: 'donut'
        };
      case 'line':
        return {
          options: getLineOptions(title, labels),
          series: [{ name: '数值', data: values }],
          chartType: 'area' // 使用面积图效果更好
        };
      default:
        return {
          options: getBarOptions(title, labels),
          series: [{ name: '数值', data: values }],
          chartType: 'bar'
        };
    }
  }, [type, title, labels, values]);

  if (!data || data.length === 0) {
    return (
      <div className="a2ui-chart-empty">
        <span>📊 暂无数据</span>
      </div>
    );
  }

  return (
    <div className="a2ui-chart-container">
      <Chart
        options={options}
        series={series}
        type={chartType}
        height={260}
        width="100%"
      />
    </div>
  );
};

export default A2UIChart;

